/**
 * Typed calls against the Natura Tagger cycle endpoints.
 * Schema mirrors src/natura_tagger/schemas/cycle.py on the backend.
 */
import { apiFetch } from "./client";
import { getConfig } from "@/config";
import { getCachedIdToken } from "@/auth/firebase";

export type CycleStatus =
  | "QUEUED"
  | "PROCESSING"
  | "READY_FOR_REVIEW"
  | "IN_REVIEW"
  | "COMPLETED"
  | "FAILED";

/** Semantic kind of an object's class label (rev03 r3-1/r3-10). */
export type ClassKind = "produto" | "elemento";

// Operational workflow state (rev03 r3-3) — orthogonal to the processing
// CycleStatus. Tracks the marca→aprova→envia flow.
export type OperationalState =
  | "teste"
  | "rascunho"
  | "pronto_para_aprovacao"
  | "aprovado"
  | "enviado";

export const OPERATIONAL_STATES: OperationalState[] = [
  "teste",
  "rascunho",
  "pronto_para_aprovacao",
  "aprovado",
  "enviado",
];

export function operationalStateLabel(state: OperationalState): string {
  switch (state) {
    case "teste":
      return "Teste";
    case "rascunho":
      return "Rascunho";
    case "pronto_para_aprovacao":
      return "Pronto p/ aprovação";
    case "aprovado":
      return "Aprovado";
    case "enviado":
      return "Enviado";
  }
}

export async function setOperationalState(
  cycleId: string,
  operationalState: OperationalState,
): Promise<{ cycleId: string; operationalState: OperationalState }> {
  return apiFetch(`/v1/cycles/${cycleId}/operational-state`, {
    method: "PATCH",
    body: JSON.stringify({ operationalState }),
  });
}

export type ObjectStatus =
  | "suggested"
  | "confirmed"
  | "manual"
  | "exception"
  | "trash";

export type ObjectType =
  | "image"
  | "tag"
  | "text"
  | "casting"
  | "ruler"
  | "stamp"
  | "manual_box";

export type BBox = { x: number; y: number; w: number; h: number };

export type AnnotationObject = {
  id: string;
  page: number;
  bbox: BBox;
  type: ObjectType;
  class: string | null;
  /** Semantic kind of `class` (rev03 r3-1): a product SKU vs a page element.
   *  Always resolved server-side (derived for legacy objects); null only when
   *  the object has no label yet. */
  classKind: ClassKind | null;
  source: "extracted" | "vision" | "manual" | "memorized";
  status: ObjectStatus;
  confidence: number | null;
  recommendedClassLabel: string | null;
  recommendationConfidence: "high" | "medium" | "low" | null;
  provenance: { producedBy: string; rule?: string | null; digest?: string | null };
};

export type Capabilities = {
  supportsConnections: boolean;
  supportsRulers: boolean;
  supportsStamps: boolean;
  supportsManualBoxes: boolean;
  supportsOverlapMerge: boolean;
  supportsAutoClassification: boolean;
};

export type CycleAnnotations = {
  contractVersion: "1.0";
  name: string;
  cycleId: string;
  status: CycleStatus;
  operationalState: OperationalState;
  producedAt: string;
  document: { pageCount: number; pages: Page[] };
  objects: AnnotationObject[];
  connections: Connection[];
  overlapPairs: OverlapPairWire[];
  manualTrashConflicts: ManualTrashConflictWire[];
  locked1At: string | null;
  locked2At: string | null;
  capabilities: Capabilities;
  /** Short-lived signed download URLs for the cycle's root artifacts.
   *  Each field is null when the artifact doesn't exist yet (e.g.
   *  annotated PDF before COMPLETED) or when the backend can't sign
   *  (local dev) — caller falls back to `assetUrl(cycleId, key)`. */
  downloads: CycleDownloads;
};

export type Lock1Summary = {
  confirmed: number;
  trash: number;
  manual: number;
  suggested: number;
  exception: number;
  pendingOverlapPairs: number;
  objectMemoryWrites: number;
  trashSignatureWrites: number;
  locked1At: string | null;
  canLock: boolean;
  blockedReasons: string[];
  warnings: string[];
};

export type Lock1Response = {
  cycleId: string;
  locked1At: string;
  objectMemoryWrites: number;
  trashSignatureWrites: number;
  // Populated when this Lock 1 was the LAST lock to run (Lock 2 already
  // done) and the cycle finalized here.
  annotatedPdfKey: string | null;
  annotatedPdfBytes: number | null;
  csvKey: string | null;
  csvBytes: number | null;
};

export type Lock2Summary = {
  needsLabel: number;
  labeled: number;
  unlabeled: number;
  labelMemoryWrites: number;
  locked2At: string | null;
  canLock: boolean;
  blockedReasons: string[];
  warnings: string[];
};

export type Lock2Response = {
  cycleId: string;
  locked2At: string;
  labelMemoryWrites: number;
  annotatedPdfKey: string | null;
  annotatedPdfBytes: number | null;
  csvKey: string | null;
  csvBytes: number | null;
};

export type Page = {
  index: number;
  widthPt: number;
  heightPt: number;
  renderKey?: string | null;
  thumbnailKey?: string | null;
  /** Short-lived signed GCS URL for the full-size page render. Populated
   *  in prod; null in local dev (fall back to `assetUrl(cycleId, renderKey)`). */
  renderUrl?: string | null;
  /** Short-lived signed GCS URL for the thumbnail. Populated in prod;
   *  null in local dev. */
  thumbnailUrl?: string | null;
};

export type CycleDownloads = {
  annotatedPdfUrl: string | null;
  cycleCsvUrl: string | null;
  originalPdfUrl: string | null;
};

/** Build a URL pointing at the gateway's asset proxy for a given storage key.
 *
 * Fallback path used only in local dev, where the GCS backend isn't
 * available so the backend can't sign URLs. In prod, callers should prefer
 * the signed URL fields returned in the cycle / list responses
 * (`page.renderUrl`, `cycle.downloads.annotatedPdfUrl`, etc.). Those go
 * straight to GCS and skip both Firebase Hosting and our Cloud Run service.
 *
 * The `?token=` query param is the only way browser `<img src>` / `<a
 * href>` can authenticate with our asset endpoint, since those tags can't
 * attach an Authorization header. AUTH_DEV_BYPASS in dev short-circuits
 * server-side, so the same URL works whether or not a token is appended.
 */
export function assetUrl(cycleId: string, key: string): string {
  const base = `${cyclesApiBase()}/v1/cycles/${cycleId}/assets/${key}`;
  const token = getCachedIdToken();
  return token ? `${base}?token=${encodeURIComponent(token)}` : base;
}

/** Browser-ready URL for an arbitrary cycle asset.
 *
 * Picks the signed URL when present (prod) and falls back to the proxied
 * asset endpoint (local dev). `signedUrl` should be the *Url field that
 * lives next to its *Key counterpart on the cycle response.
 */
export function preferredAssetUrl(
  cycleId: string,
  key: string | null | undefined,
  signedUrl: string | null | undefined,
): string | null {
  if (signedUrl) return signedUrl;
  if (!key) return null;
  return assetUrl(cycleId, key);
}

function cyclesApiBase(): string {
  // Re-read at call time so config changes (HMR) propagate.
  return getConfig().apiBaseUrl.replace(/\/$/, "");
}

export type Connection = {
  id: string;
  fromObjectId: string;
  toObjectId: string;
  kind: "element_to_product" | "text_to_sku" | "stamp_to_sku";
};

/** Link an element to the product it belongs to (rev03 r3-1). Idempotent —
 *  re-linking the same pair returns the existing connection. */
export function createConnection(
  cycleId: string,
  fromObjectId: string,
  toObjectId: string,
): Promise<Connection> {
  return apiFetch<Connection>(`/v1/cycles/${cycleId}/connections`, {
    method: "POST",
    body: JSON.stringify({ fromObjectId, toObjectId }),
  });
}

export async function deleteConnection(
  cycleId: string,
  connectionId: string,
): Promise<void> {
  await apiFetch<void>(`/v1/cycles/${cycleId}/connections/${connectionId}`, {
    method: "DELETE",
  });
}

export type OverlapDecision =
  | "pending"
  | "keep_a"
  | "keep_b"
  | "keep_both"
  | "merge";

export type OverlapPairWire = {
  pairId: string;
  page: number;
  objectAId: string;
  objectBId: string;
  iou: number;
  decision: OverlapDecision;
  resolvedAt: string | null;
  resolvedBy: string | null;
  mergedObjectId: string | null;
};

export type ManualTrashDecision =
  | "pending"
  | "manual_wins"
  | "trash_wins"
  | "both_kept";

export type ManualTrashConflictWire = {
  conflictId: string;
  page: number;
  manualObjectId: string;
  trashObjectId: string;
  iou: number;
  decision: ManualTrashDecision;
  resolvedAt: string | null;
  resolvedBy: string | null;
};

export type CreateCycleResponse = {
  cycleId: string;
  status: CycleStatus;
  contractVersion: "1.0";
  createdAt: string;
  s3: { bucket: string; prefix: string };
};

export type CycleListItem = {
  cycleId: string;
  name: string;
  pipeline: "marcador" | "statsmag";
  status: CycleStatus;
  operationalState: OperationalState;
  pageCount: number;
  createdAt: string;
  uploadedBy: string | null;
  /** Signed download URLs. Populated for COMPLETED cycles in prod; the
   *  three fields are null otherwise — list rows that show a download
   *  button should fall back to `assetUrl(cycleId, key)`. */
  downloads: CycleDownloads;
};

export type CycleListResponse = { items: CycleListItem[] };

export function listCycles(): Promise<CycleListResponse> {
  return apiFetch<CycleListResponse>("/v1/cycles");
}

export type CreateCycleOptions = {
  pipeline?: "marcador" | "statsmag";
  /** 1-based page-range string, e.g. "1-5,7,10-12". Empty/undefined = all
   *  pages (the worker keeps every page). Backend canonicalizes + validates. */
  pageFilter?: string;
  /** Called with a 0..1 fraction as the PDF bytes upload to storage. */
  onProgress?: (fraction: number) => void;
};

type UploadUrlResponse = {
  uploadId: string;
  uploadUrl: string;
  method: "PUT";
  headers: Record<string, string>;
  expiresAt: string;
  maxBytes: number;
};

/** PUT bytes via XHR so the caller can render real upload progress
 *  (fetch() can't report request-body upload progress). Resolves on a 2xx,
 *  rejects with the response text otherwise. `onProgress` gets a 0..1
 *  fraction; it stays undefined-safe for callers that don't pass one. */
function putWithProgress(
  url: string,
  method: string,
  headers: Record<string, string>,
  body: Blob,
  onProgress?: (fraction: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url);
    for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);
    xhr.upload.onprogress = (e) => {
      if (onProgress && e.lengthComputable) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(1);
        resolve();
      } else {
        reject(
          new Error(
            `Upload to storage failed (${xhr.status}): ${
              xhr.responseText || xhr.statusText || "no body"
            }`,
          ),
        );
      }
    };
    xhr.onerror = () => reject(new Error("Upload to storage failed (network error)"));
    xhr.send(body);
  });
}

/**
 * Two-step upload that bypasses Firebase Hosting's 32 MB rewrite cap.
 *
 * Step 1: ask the API for a signed URL; the response carries an opaque
 *   uploadId plus a URL the browser PUTs the PDF bytes to. In prod that
 *   URL points at storage.googleapis.com directly; in local dev it
 *   points back at the API's own staging endpoint (which writes to disk).
 *
 * Step 2: POST the cycle metadata referencing the uploadId — the API
 *   promotes the staged blob into the cycle's permanent slot and enqueues
 *   the extract job.
 *
 * Why not just POST multipart to /v1/cycles? Firebase Hosting silently
 * 500s any rewrite request body over 32 MB, and most Natura ciclos are
 * larger than that, so the direct-to-storage path is the only one that
 * works in prod for real files.
 */
export async function createCycle(
  pdf: File,
  name: string,
  opts: CreateCycleOptions = {},
): Promise<CreateCycleResponse> {
  const contentType = pdf.type || "application/pdf";
  const url = await apiFetch<UploadUrlResponse>("/v1/cycles/upload-url", {
    method: "POST",
    body: JSON.stringify({
      filename: pdf.name,
      contentType,
      sizeBytes: pdf.size,
    }),
  });

  // PUT the bytes directly to storage. This request does NOT go through
  // apiFetch — apiFetch adds the Firebase Bearer token, but GCS rejects
  // requests with extra Authorization headers when using a signed URL.
  // XHR (not fetch) so we can surface real upload progress (rev02 r2-6).
  await putWithProgress(
    url.uploadUrl,
    url.method,
    { ...url.headers },
    pdf,
    opts.onProgress,
  );

  return apiFetch<CreateCycleResponse>("/v1/cycles/from-upload", {
    method: "POST",
    body: JSON.stringify({
      uploadId: url.uploadId,
      name,
      pipeline: opts.pipeline ?? "marcador",
      pageFilter:
        opts.pageFilter && opts.pageFilter.trim() ? opts.pageFilter.trim() : null,
    }),
  });
}

export function getCycle(cycleId: string): Promise<CycleAnnotations> {
  return apiFetch<CycleAnnotations>(`/v1/cycles/${cycleId}`);
}

export type ObjectPatch = {
  status?: ObjectStatus;
  classLabel?: string | null;
  /** Geometry is editable on any source pre-Lock-1; the image hash that
   *  keys cross-cycle memory is captured at extraction and unaffected by
   *  bbox changes. Lock-1 freezes geometry on every source. */
  bbox?: BBox;
};

export function patchObject(
  cycleId: string,
  objectId: string,
  patch: ObjectPatch,
): Promise<AnnotationObject> {
  return apiFetch<AnnotationObject>(
    `/v1/cycles/${cycleId}/objects/${objectId}`,
    {
      method: "PATCH",
      body: JSON.stringify(patch),
    },
  );
}

export type CreateManualObjectBody = {
  page: number;
  bbox: BBox;
  classLabel?: string | null;
};

export function createManualObject(
  cycleId: string,
  body: CreateManualObjectBody,
): Promise<AnnotationObject> {
  return apiFetch<AnnotationObject>(`/v1/cycles/${cycleId}/objects`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function deleteObject(cycleId: string, objectId: string): Promise<void> {
  return apiFetch<void>(`/v1/cycles/${cycleId}/objects/${objectId}`, {
    method: "DELETE",
  });
}

export function resolveOverlapPair(
  cycleId: string,
  pairId: string,
  decision: OverlapDecision,
): Promise<OverlapPairWire> {
  return apiFetch<OverlapPairWire>(
    `/v1/cycles/${cycleId}/overlap-pairs/${pairId}/resolve`,
    {
      method: "POST",
      body: JSON.stringify({ decision }),
    },
  );
}

export function resolveManualTrashConflict(
  cycleId: string,
  conflictId: string,
  decision: ManualTrashDecision,
): Promise<ManualTrashConflictWire> {
  return apiFetch<ManualTrashConflictWire>(
    `/v1/cycles/${cycleId}/manual-trash-conflicts/${conflictId}/resolve`,
    {
      method: "POST",
      body: JSON.stringify({ decision }),
    },
  );
}

export function getLock1Summary(cycleId: string): Promise<Lock1Summary> {
  return apiFetch<Lock1Summary>(`/v1/cycles/${cycleId}/lock1-summary`);
}

export function postLock1(cycleId: string): Promise<Lock1Response> {
  return apiFetch<Lock1Response>(`/v1/cycles/${cycleId}/lock1`, {
    method: "POST",
  });
}

export function getLock2Summary(cycleId: string): Promise<Lock2Summary> {
  return apiFetch<Lock2Summary>(`/v1/cycles/${cycleId}/lock2-summary`);
}

export function postLock2(cycleId: string): Promise<Lock2Response> {
  return apiFetch<Lock2Response>(`/v1/cycles/${cycleId}/lock2`, {
    method: "POST",
  });
}
